import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'next-i18next';
import { basePageWrap } from '../BasePage';

import Hero from '../../components/Hero';
import ListCardPartner from '../../components/ListCardPartner';
import ListCardDmc from '../../components/ListCardDmc';
import CheckBoxFilter from '../../components/CheckBoxFilter';
import FilterDropdown from '../../components/FilterDropdown';
import Loader from '../../components/Loader';
import ListTabs from '../../components/ListTabs';

import styles from './PartnerListPage.module.scss';
import { keysToCamelFromSnake } from '../../utils/caseconverters';

const WINDOW =
    typeof window !== 'undefined'
        ? window.parent
            ? window.parent
            : window
        : null;

const PartnerListPage = ({
    hero,
    preamble,
    apiUrl,
    checkBoxFilter,
    initialItems = [],
}) => {
    const { t } = useTranslation();

    console.log(checkBoxFilter);
    const getInitialFiltersFromUrl = (checkBoxFilter) => {
        let params;

        if (WINDOW) {
            const getParams = WINDOW.location.search;
            params = new URLSearchParams(getParams);
        } else {
            params = new URLSearchParams();
        }
        params = new URLSearchParams();

        return checkBoxFilter?.filters.reduce((acc, cur) => {
            const paramVal = params.get(cur.group);
            acc[cur.group] = paramVal ? paramVal.split(',') : [];
            return acc;
        }, {});
    };

    const initialFilterGroups = getInitialFiltersFromUrl(checkBoxFilter);

    const [activeFilterGroups, setActiveFilterGroups] =
        useState(initialFilterGroups);
    const [activeSortPath, setActiveSortPath] = useState('title');
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState(initialItems);
    const [error, setError] = useState(false);
    const [activeTab, setActiveTab] = useState('regions-destinations');

    useEffect(() => {
        const handleHashChange = () => {
            const slug = window.location.hash.replace('#', '');
            if (slug === 'regions-destinations' || slug === 'dmc-incoming') {
                setActiveTab(slug);
            }
        };
        window.addEventListener('hashchange', handleHashChange);
        handleHashChange();
        return () => {
            window.removeEventListener('hashchange', handleHashChange);
        };
    }, []);

    const handleFilter = (slug, group, checked) => {
        const updatedFilterGroup = checked
            ? [...activeFilterGroups[group], slug]
            : activeFilterGroups[group].filter((x) => x !== slug);

        const nextActiveFilterGroups = {
            ...activeFilterGroups,
            [group]: updatedFilterGroup,
        };

        setActiveFilterGroups(nextActiveFilterGroups);
        fetchItems(nextActiveFilterGroups);
    };

    const handleSort = (path) => {
        setActiveSortPath(path);
        fetchItems(activeFilterGroups, path);
    };

    const fetchItems = (nextActiveFilterGroups, sortPath = activeSortPath) => {
        const filterGroupsWithSort = {
            ...nextActiveFilterGroups,
            o: sortPath,
        };

        const qs = Object.entries(filterGroupsWithSort)
            .map(([key, value]) => {
                if (value.length === 0) {
                    return `${key}=`;
                }
                return `${key}=${Array.isArray(value) ? value.join(',') : value}`;
            })
            .join('&');

        if (WINDOW) {
            WINDOW.history.replaceState(null, null, `?${qs}`);
        }

        const url = `${apiUrl}?${qs}`;
        fetch(url)
            .then((res) => res.json())
            .then(
                (res) => {
                    const formattedResponse = keysToCamelFromSnake(res);
                    setItems(formattedResponse);
                    setTimeout(() => setLoading(false), 1500);
                },
                () => {
                    setTimeout(() => setLoading(false), 1500);
                    setError(true);
                }
            );
    };

    const dmcItems = items.filter((item) =>
        item.type?.includes('DMC/Incoming')
    );

    const partnerItems = initialItems.filter(
        (item) => !item.type?.includes('DMC/Incoming')
    );

    const excludeFilterGroups = ['types', 'regions', 'county'];

    const tabs = [
        {
            id: 'regions-destinations',
            tabTitle: 'Regions & destinations',
            content: (
                <>
                    <h2 className="sr-only">Regions & destinations</h2>
                    {loading && <Loader />}

                    {!loading && partnerItems.length > 0 && (
                        <ListCardPartner
                            items={partnerItems}
                            activeSortPath={activeSortPath}
                            isActiveTab={activeTab === 'regions-destinations'}
                            onSort={handleSort}
                        />
                    )}

                    {!loading && error && (
                        <div className={styles['PartnerListPage__Error']}>
                            <h3
                                className={
                                    styles['PartnerListPage__Error__Title']
                                }
                            >
                                {t('partnerListPage.errorTitle')}
                            </h3>
                            <p
                                className={
                                    styles['PartnerListPage__Error__Text']
                                }
                            >
                                {t('partnerListPage.errorText')}
                            </p>
                        </div>
                    )}
                </>
            ),
        },
        {
            id: 'dmc-incoming',
            tabTitle: 'DMC/Incoming',
            content: (
                <>
                    <h2 className="sr-only">DMC/Incoming</h2>
                    <div className={styles['PartnerListPage__FilterWrapper']}>
                        {checkBoxFilter &&
                            checkBoxFilter.filters
                                .filter(
                                    (filter) =>
                                        filter.items &&
                                        filter.items.length > 0 &&
                                        !excludeFilterGroups.includes(
                                            filter.group
                                        )
                                )
                                .map((filter, index) => (
                                    <FilterDropdown
                                        key={index}
                                        title={filter.title}
                                        items={filter.items}
                                        onChange={(slug, checked) => {
                                            handleFilter(
                                                slug,
                                                filter.group,
                                                checked
                                            );
                                        }}
                                    />
                                ))}
                    </div>
                    {loading && <Loader />}

                    {!loading && dmcItems.length > 0 && (
                        <ListCardDmc
                            items={dmcItems}
                            activeSortPath={activeSortPath}
                            isActiveTab={activeTab === 'dmc-incoming'}
                            onSort={handleSort}
                        />
                    )}

                    {!loading && error && (
                        <div className={styles['PartnerListPage__Error']}>
                            <h3
                                className={
                                    styles['PartnerListPage__Error__Title']
                                }
                            >
                                {t('partnerListPage.errorTitle')}
                            </h3>
                            <p
                                className={
                                    styles['PartnerListPage__Error__Text']
                                }
                            >
                                {t('partnerListPage.errorText')}
                            </p>
                        </div>
                    )}
                </>
            ),
        },
    ];

    const handleTabSwitch = (tabId) => {
        setActiveTab(tabId);

        if (tabId === 'regions-destinations') {
            const url = new URL(window.location.href);
            url.search = '';
            url.hash = `#${tabId}`;
            window.history.replaceState(null, '', url.toString());
        } else {
            window.history.replaceState(null, '', `#${tabId}`);
        }
    };

    return (
        <div className={styles['PartnerListPage']}>
            <Hero {...hero} />
            <div className={styles['PartnerListPage__Container']}>
                {!!preamble && (
                    <p className={styles['PartnerListPage__Preamble']}>
                        {preamble}
                    </p>
                )}
                {tabs && tabs.length > 0 && (
                    <ListTabs
                        tabs={tabs}
                        activeTab={activeTab}
                        onTabSwitch={handleTabSwitch}
                    />
                )}
            </div>
        </div>
    );
};

PartnerListPage.propTypes = {
    hero: PropTypes.object,
    items: PropTypes.array,
    checkBoxFilter: PropTypes.object,
};

PartnerListPage.defaultProps = {
    hero: {},
    items: [],
    checkBoxFilter: null,
};

export default basePageWrap(PartnerListPage);
